import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const FAQPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const faqs: FAQItem[] = [
    // Account & Registration
    {
      question: "How do I create an account?",
      answer: "Click on the 'Register' button in the top navigation. Fill in your details including name, email, password, and select whether you want to be a student or instructor. Click 'Create Account' to complete registration.",
      category: "account"
    },
    {
      question: "Can I change my account type after registration?",
      answer: "Currently, account types (student/instructor) cannot be changed after registration. Please contact support if you need to change your account type.",
      category: "account"
    },
    {
      question: "I forgot my password. How do I reset it?",
      answer: "Click on the 'Login' button and then click 'Forgot Password?' link. Enter your email address and we'll send you a password reset link.",
      category: "account"
    },
    {
      question: "How do I update my profile information?",
      answer: "Log in to your account and go to your profile page. You can update your personal information, bio, and contact details there.",
      category: "account"
    },

    // Courses & Learning
    {
      question: "How do I enroll in a course?",
      answer: "Browse the course catalog, click on a course you're interested in, and click the 'Enroll' button on the course details page. You must be logged in as a student to enroll.",
      category: "courses"
    },
    {
      question: "How do I track my learning progress?",
      answer: "As a student, you can view your enrolled courses and progress in your student dashboard. Progress is automatically tracked as you complete lessons.",
      category: "courses"
    },
    {
      question: "Can I download course materials?",
      answer: "Yes, most course materials can be downloaded for offline viewing. Look for the download button next to each resource in your course.",
      category: "courses"
    },
    {
      question: "How do I access my enrolled courses?",
      answer: "Log in to your account and go to your student dashboard. All your enrolled courses will be listed there with your progress.",
      category: "courses"
    },

    // Instructor Tools
    {
      question: "How do I create a course as an instructor?",
      answer: "Log in as an instructor, go to your instructor dashboard, and click 'Create New Course'. Fill in the course details including title, description, category, and difficulty level.",
      category: "instructor"
    },
    {
      question: "How do I upload course materials?",
      answer: "Instructors can upload course materials when creating or editing lessons. Supported file types include PDF, DOC, PPT, and various image formats.",
      category: "instructor"
    },
    {
      question: "How do I manage my students?",
      answer: "In your instructor dashboard, you can view all enrolled students, track their progress, and communicate with them through the platform.",
      category: "instructor"
    },
    {
      question: "Can I edit my course after publishing?",
      answer: "Yes, you can edit your course content at any time. Go to your instructor dashboard, find the course, and click 'Edit' to make changes.",
      category: "instructor"
    },

    // Payment & Billing
    {
      question: "What payment methods are accepted?",
      answer: "Currently, all courses are free. Payment integration will be added in future updates.",
      category: "payment"
    },
    {
      question: "Are there any hidden fees?",
      answer: "No, there are no hidden fees. All costs are clearly displayed before you enroll in any course.",
      category: "payment"
    },
    {
      question: "Can I get a refund?",
      answer: "Since all courses are currently free, refunds are not applicable. When paid courses are introduced, refunds will be available within 30 days of purchase.",
      category: "payment"
    },

    // Technical Support
    {
      question: "What browsers are supported?",
      answer: "We support all modern browsers including Chrome, Firefox, Safari, and Edge. For the best experience, we recommend using the latest version of your browser.",
      category: "technical"
    },
    {
      question: "I'm having trouble with video playback. What should I do?",
      answer: "Check your internet connection and try refreshing the page. If the problem persists, try using a different browser or contact our support team.",
      category: "technical"
    },
    {
      question: "How do I contact support?",
      answer: "You can contact our support team through the Contact page or email us directly at support@edulearn.com",
      category: "support"
    },
    {
      question: "What are the system requirements?",
      answer: "You need a modern web browser, stable internet connection, and speakers or headphones for video content. No special software installation is required.",
      category: "technical"
    }
  ];

  const categories = [
    { id: 'all', name: 'All Questions' },
    { id: 'account', name: 'Account & Registration' },
    { id: 'courses', name: 'Courses & Learning' },
    { id: 'instructor', name: 'Instructor Tools' },
    { id: 'payment', name: 'Payment & Billing' },
    { id: 'technical', name: 'Technical Support' },
    { id: 'support', name: 'Support & Contact' }
  ];

  const filteredFAQs = faqs.filter(faq => 
    selectedCategory === 'all' || faq.category === selectedCategory
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h1>
          <p className="text-lg text-gray-600">
            Find quick answers to common questions about using EduLearn
          </p>
        </div>

        {/* Category Filter */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                  selectedCategory === category.id
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              {selectedCategory === 'all' ? 'All Questions' : categories.find(c => c.id === selectedCategory)?.name}
              <span className="text-sm font-normal text-gray-500 ml-2">
                ({filteredFAQs.length} questions)
              </span>
            </h2>
          </div>
          
          <div className="divide-y divide-gray-200">
            {filteredFAQs.map((faq, index) => (
              <div key={index} className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>

          {filteredFAQs.length === 0 && (
            <div className="p-8 text-center">
              <div className="text-gray-400 mb-4">
                <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.47-.881-6.08-2.33" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No questions found</h3>
              <p className="text-gray-600">Try selecting a different category or browse all questions</p>
            </div>
          )}
        </div>

        {/* Contact Support */}
        <div className="mt-8 bg-primary-50 rounded-lg p-6">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Still need help?</h3>
            <p className="text-gray-600 mb-4">
              Can't find what you're looking for? Our support team is here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors duration-200"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Contact Support
              </Link>
              <a
                href="mailto:support@edulearn.com"
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors duration-200"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Email Support
              </a>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <Link
            to="/help"
            className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow duration-200"
          >
            <div className="flex items-center">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Help Center</h3>
                <p className="text-sm text-gray-600">Detailed guides and tutorials</p>
              </div>
            </div>
          </Link>

          <Link
            to="/contact"
            className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow duration-200"
          >
            <div className="flex items-center">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Contact Us</h3>
                <p className="text-sm text-gray-600">Get in touch with our team</p>
              </div>
            </div>
          </Link>

          <Link
            to="/about"
            className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow duration-200"
          >
            <div className="flex items-center">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="font-medium text-gray-900">About Us</h3>
                <p className="text-sm text-gray-600">Learn more about EduLearn</p>
              </div>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
